/**
 * B.3.c — Admin-consent landing banner.
 *
 * Catches the `?admin_consent=True&tenant=…` redirect that Entra posts
 * after an admin grants tenant-wide consent via /adminconsent. Today
 * (pre-B.3.c) this dropped the user on the bare Win95 demo desktop with
 * no acknowledgment; the banner is the in-milestone fix. See
 * `feedback_admin_consent_landing_ux.md`.
 *
 * The banner renders centered as a modal (backdrop dismiss) so it isn't
 * clipped by the user's browser chrome. Auto-clears its query string on
 * dismiss so a refresh doesn't keep showing it.
 *
 * Hidden when the URL carries no `admin_consent` param.
 */

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { testIds } from '../../lib/testIds';
import {
  parseAdminConsentLanding,
  readAdminConsentIntent,
  clearAdminConsentIntent,
  type AdminConsentIntent,
} from '../../lib/onboard/adminConsentLanding';

interface KnownPortal {
  slug: string;
  displayName: string;
}

interface AdminConsentLandingBannerProps {
  defaultPortals?: KnownPortal[];
}

export function AdminConsentLandingBanner({
  defaultPortals,
}: AdminConsentLandingBannerProps): ReactElement | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [intent, setIntent] = useState<AdminConsentIntent | null>(null);

  const landing = useMemo(
    () => parseAdminConsentLanding(location.searchStr ?? ''),
    [location.searchStr],
  );

  useEffect(() => {
    if (!landing.detected) {return;}
    if (typeof window === 'undefined') {return;}
    const captured = readAdminConsentIntent(window.sessionStorage);
    setIntent(captured);
  }, [landing.detected]);

  if (!landing.detected || dismissed) {return null;}

  const portals: KnownPortal[] =
    intent?.knownSlugs?.map((slug) => ({
      slug,
      displayName: slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    })) ??
    defaultPortals ??
    [];

  const handleDismiss = (): void => {
    if (typeof window !== 'undefined') {
      clearAdminConsentIntent(window.sessionStorage);
    }
    setDismissed(true);
    void navigate({ to: '.', replace: true, search: () => ({}) });
  };

  const tenantLabel = landing.tenantId
    ? `tenant ${landing.tenantId.slice(0, 8)}…`
    : 'your tenant';

  // Centered modal — `inset-0 + flex items-center justify-center` keeps the
  // dialog in the visual middle of the viewport on any size. The backdrop
  // click closes the modal; the inner card stops propagation.
  const backdropClass =
    'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4';

  if (!landing.success) {
    return (
      <div
        data-testid={testIds.adminConsentLanding.banner}
        className={backdropClass}
        onClick={handleDismiss}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded border-2 border-red-600 bg-red-50 px-5 py-4 text-sm shadow-xl"
        >
          <div className="text-base font-bold text-red-800">Admin consent failed</div>
          <div className="mt-2 text-red-900">
            {landing.errorCode ? <code className="font-mono">{landing.errorCode}</code> : null}
            {landing.errorDescription ? ` — ${landing.errorDescription}` : null}
          </div>
          <button
            type="button"
            data-testid={testIds.adminConsentLanding.dismiss}
            onClick={handleDismiss}
            className="mt-3 cursor-pointer rounded border border-red-700 bg-red-200 px-3 py-1.5 text-xs font-medium"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testIds.adminConsentLanding.banner}
      className={backdropClass}
      onClick={handleDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded border-2 border-green-700 bg-white px-5 py-5 text-sm shadow-xl"
      >
        <div className="text-base font-semibold text-green-900">
          Admin consent granted
        </div>
        <div className="mt-1 text-xs text-gray-700">
          for{' '}
          <span data-testid={testIds.adminConsentLanding.tenantName} className="font-mono">
            {tenantLabel}
          </span>
        </div>
        {intent?.returnTo ? (
          <div className="mt-4">
            <Link
              to={intent.returnTo}
              className="inline-block rounded border border-[#404040] bg-[#d0d0d0] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#e0e0e0]"
              onClick={handleDismiss}
            >
              Continue setup →
            </Link>
          </div>
        ) : portals.length > 0 ? (
          <div className="mt-4 text-gray-900">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-600">
              Open a portal
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {portals.map((p) => (
                <Link
                  key={p.slug}
                  to="/c/$clientSlug"
                  params={{ clientSlug: p.slug }}
                  data-testid={testIds.adminConsentLanding.deeplink(p.slug)}
                  className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:border-green-700 hover:bg-green-50"
                  onClick={handleDismiss}
                >
                  {p.displayName}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-gray-900">
            Your Microsoft account now has access. You can sign in to any portal.
          </div>
        )}
        <button
          type="button"
          data-testid={testIds.adminConsentLanding.dismiss}
          onClick={handleDismiss}
          className="mt-4 cursor-pointer text-xs text-gray-600 underline"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
